import React, { useState, useEffect, useMemo } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineChartPie,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineNoSymbol
} from 'react-icons/hi2';
import { authService, machineService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';
import './OEEThresholds.css';

const OEEThresholds = () => {
  const [thresholds, setThresholds] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);
  
  const [formData, setFormData] = useState({
    machineId: '',
    targetOee: '85',
    warningOee: '65',
    criticalOee: '50',
    status: 'Active',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view OEE thresholds');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [thresholdData, machineData] = await Promise.all([
        masterDataService.getOeeThresholds(tenantId),
        machineService.getAllMachines()
      ]);
      setThresholds(thresholdData || []);
      setMachines(machineData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading OEE thresholds:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredThresholds = thresholds.filter((t) => {
    const machine = machines.find(m => m.machineId === t.machineId);
    const machineName = machine ? machine.machineName : 'Global Default';
    return machineName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleAdd = () => {
    setEditingThreshold(null);
    setFormData({
      machineId: '',
      targetOee: '85',
      warningOee: '65',
      criticalOee: '50',
      status: 'Active',
    });
    setFormErrors({});
    setShowDrawer(true);
  };

  const handleEdit = (threshold) => {
    setEditingThreshold(threshold);
    setFormData({
      machineId: threshold.machineId ? threshold.machineId.toString() : '',
      targetOee: threshold.targetOee?.toString() || '85',
      warningOee: threshold.warningOee?.toString() || '65',
      criticalOee: threshold.criticalOee?.toString() || '50',
      status: threshold.status || 'Active',
    });
    setFormErrors({});
    setShowDrawer(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteOeeThreshold(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete threshold. Please try again.');
      console.error('Error deleting OEE threshold:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const target = parseFloat(formData.targetOee);
    const warning = parseFloat(formData.warningOee);
    const critical = parseFloat(formData.criticalOee);

    if (isNaN(target) || target < 0 || target > 100) errors.targetOee = 'Target must be between 0-100';
    if (isNaN(warning) || warning < 0 || warning > 100) errors.warningOee = 'Warning must be between 0-100';
    if (isNaN(critical) || critical < 0 || critical > 100) errors.criticalOee = 'Critical must be between 0-100';

    if (!errors.targetOee && !errors.warningOee && target <= warning) {
      errors.targetOee = 'Target must be greater than Warning';
    }
    if (!errors.warningOee && !errors.criticalOee && warning <= critical) {
      errors.warningOee = 'Warning must be greater than Critical';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError('');

      const thresholdData = {
        tenantId: tenantId,
        machineId: formData.machineId ? parseInt(formData.machineId) : null,
        targetOee: parseFloat(formData.targetOee),
        warningOee: parseFloat(formData.warningOee),
        criticalOee: parseFloat(formData.criticalOee),
        status: formData.status,
      };

      if (editingThreshold) {
        thresholdData.thresholdId = editingThreshold.thresholdId;
        await masterDataService.updateOeeThreshold(editingThreshold.thresholdId, thresholdData);
      } else {
        await masterDataService.createOeeThreshold(thresholdData);
      }

      await loadData();
      setShowDrawer(false);
      setEditingThreshold(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save threshold.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingThreshold(null);
    setFormErrors({});
  };

  const getMachineName = (machineId) => {
    if (!machineId) return 'Global Default';
    const machine = machines.find(m => m.machineId === machineId);
    return machine ? machine.machineName : 'Global Default';
  };

  if (loading) {
    return <PageLoading message="Loading OEE thresholds..." />;
  }

  return (
    <div className="oee-thresholds-page">
      <div className="page-header">
        <div className="header-content">
          <h1>OEE Thresholds</h1>
          <p>Configure color-coded performance indicators (Green/Yellow/Red)</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Threshold
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container kizantable">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search by machine name..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            <span className="count-badge">{filteredThresholds.length}</span> {filteredThresholds.length === 1 ? 'definition' : 'definitions'}
          </div>
        </div>

        {filteredThresholds.length === 0 ? (
          <div className="empty-state">
            <HiOutlineChartPie className="empty-icon" />
            <h3>No OEE thresholds defined</h3>
            <p>Define target and warning levels to enable color-coded reporting</p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus className="btn-icon" />
                Define First Threshold
              </button>
            )}
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Machine / Scope</th>
                  <th>Good (Target)</th>
                  <th>Warning</th>
                  <th>Critical</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredThresholds.map((t) => (
                  <tr key={t.thresholdId}>
                    <td className="asset-cell">{getMachineName(t.machineId)}</td>
                    <td>
                      <span className="threshold-badge good">
                        <HiOutlineCheckCircle /> {t.targetOee}%+
                      </span>
                    </td>
                    <td>
                      <span className="threshold-badge warning">
                        <HiOutlineExclamationTriangle /> {t.warningOee}% - {t.targetOee}%
                      </span>
                    </td>
                    <td>
                      <span className="threshold-badge critical">
                        <HiOutlineNoSymbol /> Below {t.warningOee}%
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${t.status?.toLowerCase()}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon-edit" onClick={() => handleEdit(t)} title="Edit">
                          <HiOutlinePencil />
                        </button>
                        <button className="btn-icon-delete" onClick={() => setDeleteConfirm(t)} title="Delete">
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SharedModal
        isOpen={showDrawer}
        onClose={handleCloseDrawer}
        title={editingThreshold ? 'Edit Threshold' : 'New Threshold'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseDrawer}>Cancel</button>
            <button type="submit" form="oee-threshold-form" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : editingThreshold ? 'Update Threshold' : 'Save Threshold'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="oee-threshold-form" className="drawer-content" onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="machineId">Machine Scope</label>
                <select
                  id="machineId"
                  value={formData.machineId}
                  onChange={(e) => setFormData({ ...formData, machineId: e.target.value })}
                >
                  <option value="">Global Default</option>
                  {machines.map(m => (
                    <option key={m.machineId} value={m.machineId}>
                      {m.machineName}
                    </option>
                  ))}
                </select>
                <p className="warning-text">Leave empty to set as a global default threshold.</p>
              </div>

              <div className="threshold-input-group">
                <div className="form-group threshold-field">
                  <label>Target (Green)</label>
                  <input
                    type="number"
                    value={formData.targetOee}
                    onChange={(e) => setFormData({ ...formData, targetOee: e.target.value })}
                    className={formErrors.targetOee ? 'error' : ''}
                    min="0" max="100" step="0.1"
                  />
                  {formErrors.targetOee && <span className="field-error">{formErrors.targetOee}</span>}
                </div>
                <div className="form-group threshold-field">
                  <label>Warning (Yellow)</label>
                  <input
                    type="number"
                    value={formData.warningOee}
                    onChange={(e) => setFormData({ ...formData, warningOee: e.target.value })}
                    className={formErrors.warningOee ? 'error' : ''}
                    min="0" max="100" step="0.1"
                  />
                  {formErrors.warningOee && <span className="field-error">{formErrors.warningOee}</span>}
                </div>
                <div className="form-group threshold-field">
                  <label>Critical (Red)</label>
                  <input
                    type="number"
                    value={formData.criticalOee}
                    onChange={(e) => setFormData({ ...formData, criticalOee: e.target.value })}
                    className={formErrors.criticalOee ? 'error' : ''}
                    min="0" max="100" step="0.1"
                  />
                  {formErrors.criticalOee && <span className="field-error">{formErrors.criticalOee}</span>}
                </div>
              </div>

              <div className="threshold-preview">
                <span>Preview:</span>
                <div className="preview-bar">
                  <div className="bar-segment bar-critical" style={{ width: `${formData.warningOee}%` }}></div>
                  <div className="bar-segment bar-warning" style={{ width: `${formData.targetOee - formData.warningOee}%` }}></div>
                  <div className="bar-segment bar-good" style={{ width: `${100 - formData.targetOee}%` }}></div>
                </div>
                <span>100%</span>
              </div>

              <div className="form-group mt-6">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.thresholdId)}>Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete this OEE threshold definition?</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default OEEThresholds;
